import React, { useState, useEffect, useRef, useMemo } from "react";

const LEVELS = [
  { id: 1, notches: 24, label: "CALIBRATION α" },
  { id: 2, notches: 36, label: "CALIBRATION β" },
  { id: 3, notches: 48, label: "CALIBRATION γ" },
];

const RING_COLORS = [
  "#22d3ee", "#d946ef", "#fbbf24", "#34d399", "#818cf8", "#f97316",
];
function getColor(i) { return RING_COLORS[i % RING_COLORS.length]; }

const CX = 250, CY = 250;
const INNER_R = 44, RING_W = 20, RING_GAP = 5;

function polarToXY(r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: CX + r * Math.cos(rad), y: CY + r * Math.sin(rad) };
}
function fullCircle(r) {
  const t = polarToXY(r, 0), b = polarToXY(r, 180);
  return `M ${t.x} ${t.y} A ${r} ${r} 0 1 1 ${b.x} ${b.y} A ${r} ${r} 0 1 1 ${t.x} ${t.y}`;
}
function arc(r, a0, a1) {
  const s = polarToXY(r, a0), e = polarToXY(r, a1);
  const span = ((a1 - a0) + 360) % 360;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${span > 180 ? 1 : 0} 1 ${e.x} ${e.y}`;
}
function crankToAngle(crank, notches) {
  const n = ((crank % notches) + notches) % notches;
  return (n / notches) * 360;
}
function ringR(i) { return INNER_R + i * (RING_W + RING_GAP); }

// ─── LCG déterministe ─────────────────────────────────────────────────────────
function lcg(seed) {
  let s = seed | 0;
  return () => {
    s = (Math.imul(1664525, s) + 1013904223) | 0;
    return (s >>> 0) / 0xFFFFFFFF;
  };
}

const ALL_FORMULAS = [
  { label: "A + B + C + D", id: "sum" },
  { label: "A − B + C − D", id: "alt" },
  { label: "A × B − C + D", id: "mul" },
  { label: "(A+B+C+D) ÷ 2 ↑", id: "half" },
  { label: "A + B − C + D", id: "sum3" },
  { label: "A² − B + C − D", id: "sq" },
];
const CORRECT_FORMULA_BY_LEVEL = { 1: "sum", 2: "alt", 3: "half" };

function buildHintCards(level, notches) {
  const correctId = CORRECT_FORMULA_BY_LEVEL[level];
  const rand = lcg(notches * 100 + level * 7);
  const others = ALL_FORMULAS.filter(f => f.id !== correctId);
  for (let i = others.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [others[i], others[j]] = [others[j], others[i]];
  }
  const distractors = others.slice(0, 3);
  const usedCounts = new Set([notches]);
  const distractorCounts = distractors.map(() => {
    let count, attempts = 0;
    do { count = 12 + Math.floor(rand() * 40); attempts++; } while (usedCounts.has(count) && attempts < 100);
    usedCounts.add(count);
    return count;
  });
  const cards = [
    { formula: ALL_FORMULAS.find(f => f.id === correctId), sticks: notches, correct: true },
    { formula: distractors[0], sticks: distractorCounts[0], correct: false },
    { formula: distractors[1], sticks: distractorCounts[1], correct: false },
    { formula: distractors[2], sticks: distractorCounts[2], correct: false },
  ];
  for (let i = cards.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [cards[i], cards[j]] = [cards[j], cards[i]];
  }
  return cards;
}

// ─── Bâtons de comptage — multi-lignes ────────────────────────────────────────
// Découpe les bâtons en rangées de MAX_PER_ROW pour éviter le débordement
const MAX_GROUPS_PER_ROW = 5; // groupes de 5 par rangée = 25 bâtons max par ligne

function TallyRow({ groups, remainder, color }) {
  const GAP = 3;
  const GGROUP = 6;
  const H = 13;
  const W_STICK = 2.5;
  const groupWidth = 4 * W_STICK + 3 * GAP + 5;
  const totalGroups = groups + (remainder > 0 ? 1 : 0);
  const totalWidth = Math.max(1, totalGroups * (groupWidth + GGROUP) - GGROUP);
  const svgH = H + 6;
  const elements = [];
  let x = 0;

  for (let g = 0; g < groups; g++) {
    const gx = x;
    for (let s = 0; s < 4; s++) {
      const sx = gx + s * (W_STICK + GAP);
      elements.push(
        <line key={`g${g}s${s}`}
          x1={sx + W_STICK / 2} y1={2} x2={sx + W_STICK / 2} y2={H + 2}
          stroke={color} strokeWidth={W_STICK} strokeLinecap="round" opacity={0.9} />
      );
    }
    const diagX1 = gx - 3, diagX2 = gx + 4 * (W_STICK + GAP) + 2;
    elements.push(
      <line key={`g${g}d`}
        x1={diagX1} y1={H + 2} x2={diagX2} y2={2}
        stroke={color} strokeWidth={W_STICK} strokeLinecap="round" opacity={0.9} />
    );
    x += groupWidth + GGROUP;
  }
  for (let s = 0; s < remainder; s++) {
    const sx = x + s * (W_STICK + GAP);
    elements.push(
      <line key={`r${s}`}
        x1={sx + W_STICK / 2} y1={2} x2={sx + W_STICK / 2} y2={H + 2}
        stroke={color} strokeWidth={W_STICK} strokeLinecap="round" opacity={0.9} />
    );
  }

  return (
    <svg width={totalWidth} height={svgH} style={{ display: "block", overflow: "visible" }}>
      {elements}
    </svg>
  );
}

// Découpe le total en rangées de MAX_GROUPS_PER_ROW groupes
function TallyMarks({ count, color }) {
  const totalGroups = Math.floor(count / 5);
  const remainder = count % 5;

  const rows = [];
  let groupsLeft = totalGroups;
  let rowIdx = 0;

  while (groupsLeft > 0 || (rowIdx === 0 && remainder > 0)) {
    const groupsInRow = Math.min(groupsLeft, MAX_GROUPS_PER_ROW);
    const isLastRow = groupsLeft - groupsInRow === 0;
    const rem = isLastRow ? remainder : 0;

    rows.push(
      <TallyRow
        key={rowIdx}
        groups={groupsInRow}
        remainder={rem}
        color={color}
      />
    );
    groupsLeft -= groupsInRow;
    rowIdx++;
    // Si aucun groupe mais des unités restantes, on les met sur la première ligne
    if (groupsInRow === 0) break;
  }

  // Cas : count < 5, juste des unités
  if (rows.length === 0 && remainder > 0) {
    rows.push(<TallyRow key={0} groups={0} remainder={remainder} color={color} />);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 3 }}>
      {rows}
    </div>
  );
}

// ─── Card d'indice ─────────────────────────────────────────────────────────────
function HintCard({ card, notches, revealed }) {
  const isCorrect = card.correct;
  const neonColor = revealed && isCorrect ? "#34d399"
    : revealed && !isCorrect ? "#1a3040"
    : "#22d3ee";
  const textColor = revealed && !isCorrect ? "#1a3a4a" : neonColor;
  const bg = revealed && isCorrect ? "rgba(52,211,153,0.08)"
    : revealed && !isCorrect ? "rgba(0,10,20,0.4)"
    : "rgba(34,211,238,0.05)";
  const border = `1px solid ${neonColor}${revealed && !isCorrect ? "20" : "40"}`;
  const shadow = revealed && isCorrect ? `0 0 16px #34d39940` : "none";

  return (
    <div style={{
      flex: 1, minWidth: 0, padding: "8px 10px", borderRadius: 8,
      background: bg, border, boxShadow: shadow,
      display: "flex", flexDirection: "column", gap: 6,
      transition: "all 0.4s",
    }}>
      {/* Formule */}
      <div style={{
        fontSize: "10px", fontWeight: "800", letterSpacing: "0.05em",
        color: textColor, fontFamily: "ui-monospace, monospace",
        textAlign: "center",
        opacity: revealed && !isCorrect ? 0.3 : 1,
      }}>
        {card.formula.label}
      </div>

      {/* Bâtons — wrap automatique si trop nombreux */}
      <div style={{
        display: "flex", justifyContent: "center", alignItems: "flex-start",
        width: "100%", overflow: "hidden",
        opacity: revealed && !isCorrect ? 0.2 : 0.85,
      }}>
        <TallyMarks count={card.sticks} color={textColor} />
      </div>
    </div>
  );
}

// ─── Anneau ───────────────────────────────────────────────────────────────────
function Ring({ idx, notches, currentCrank, pseudo, completed, spinAngle }) {
  const r = ringR(idx), color = getColor(idx);
  const angle = spinAngle !== undefined ? spinAngle : crankToAngle(currentCrank, notches);
  const SPAN = 13;
  const tip = polarToXY(r + RING_W / 2 + 5, angle), base = polarToXY(r - RING_W / 2 - 5, angle);
  const lp = polarToXY(r, 0);
  return (
    <g>
      <path d={fullCircle(r)} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={RING_W} />
      <path d={fullCircle(r)} fill="none" stroke={color} strokeWidth={RING_W - 7} opacity={completed ? 0.55 : 0.08} />
      <path d={arc(r, angle - SPAN / 2, angle + SPAN / 2)} fill="none"
        stroke={color} strokeWidth={RING_W - 2} strokeLinecap="round" opacity={0.98}
        style={{ filter: `drop-shadow(0 0 8px ${color}) drop-shadow(0 0 20px ${color}55)` }} />
      <line x1={base.x} y1={base.y} x2={tip.x} y2={tip.y} stroke={color} strokeWidth="3" strokeLinecap="round" opacity={0.95} />
      <text x={lp.x} y={lp.y - RING_W / 2 - 11} textAnchor="middle" dominantBaseline="middle"
        fontSize="11" fontWeight="800" fill={color} opacity={0.9}
        style={{ fontFamily: "ui-monospace, monospace" }}>{pseudo}</text>
    </g>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function RotaryLock({
  myVariant, playerCount, lockState, pseudoByVariant, onRotate, onTest, socketRef, onScrollHintChange,
}) {
  const [timeLeft, setTimeLeft] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [spinAngles, setSpinAngles] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testPending, setTestPending] = useState(false);
  const [cardsRevealed, setCardsRevealed] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const rightPanelRef = useRef(null);
  const spinRafRef = useRef(null);
  const spinStartRef = useRef(null);
  const prevLevelRef = useRef(null);

  const level = lockState?.level ?? 1;
  const cfg = LEVELS[Math.min(level - 1, LEVELS.length - 1)];
  const { notches } = cfg;
  const cranks = lockState?.cranks || {};
  const tableRows = lockState?.tableRows || {};
  const phase = lockState?.phase || "idle";
  const memorizeEndsAt = lockState?.memorizeEndsAt || null;
  const allComplete = lockState?.allComplete === true;

  const hintCards = useMemo(() => buildHintCards(level, notches), [level, notches]);

  useEffect(() => { setCardsRevealed(false); }, [level]);

  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket) return;
    const onResult = ({ success }) => {
      setTestPending(false);
      if (!success) {
        setTestResult("fail");
        startSpinAnimation();
        setTimeout(() => setTestResult(null), 1700);
      } else {
        setCardsRevealed(true);
      }
    };
    socket.on("stage3:monitor:test:result", onResult);
    return () => { socket.off("stage3:monitor:test:result", onResult); };
  }, [socketRef]);

  function startSpinAnimation() {
    const DURATION = 1600;
    const speeds = [2.1, 1.6, 2.8, 1.3, 2.4, 1.8];
    if (spinRafRef.current) cancelAnimationFrame(spinRafRef.current);
    spinStartRef.current = performance.now();
    const animate = (now) => {
      const elapsed = now - spinStartRef.current;
      const t = Math.min(elapsed / DURATION, 1);
      const integral = t - t * t + (t * t * t) / 3;
      const angles = {};
      for (let i = 0; i < 6; i++) {
        const maxTurns = speeds[i] * (DURATION / 1000);
        angles[i] = (maxTurns * 3 * integral * 360) % 360;
      }
      setSpinAngles(angles);
      if (t < 1) { spinRafRef.current = requestAnimationFrame(animate); }
      else { setSpinAngles(null); spinRafRef.current = null; }
    };
    spinRafRef.current = requestAnimationFrame(animate);
  }

  useEffect(() => {
    return () => { if (spinRafRef.current) cancelAnimationFrame(spinRafRef.current); };
  }, []);

  // Indicateur de scroll — apparaît si contenu scrollable, disparaît au bas
  useEffect(() => {
    const el = rightPanelRef.current;
    if (!el) return;
    const check = () => {
      const canScroll = el.scrollHeight > el.clientHeight + 4;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
      const hint = canScroll && !atBottom;
      setShowScrollHint(hint);
      onScrollHintChange?.(hint);
    };
    check();
    el.addEventListener("scroll", check);
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", check); ro.disconnect(); };
  }, []);

  useEffect(() => {
    if (phase !== "memorize" || !memorizeEndsAt) { setTimeLeft(null); return; }
    const tick = () => setTimeLeft(Math.max(0, Math.ceil((memorizeEndsAt - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [phase, memorizeEndsAt]);

  useEffect(() => {
    if (prevLevelRef.current !== null && level !== prevLevelRef.current) {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 1600);
    }
    prevLevelRef.current = level;
  }, [level]);

  const myColor = getColor(Number(myVariant) - 1);
  const outerR = ringR(playerCount - 1) + RING_W / 2 + 24;
  const isSpinning = spinAngles !== null;
  const canTest = phase === "active" && !allComplete && !testPending && !isSpinning;

  function handleTest() {
    if (!canTest) return;
    setTestPending(true);
    onTest?.();
    setTimeout(() => setTestPending(false), 3000);
  }

  const rows = useMemo(() => {
    return Array.from({ length: playerCount }, (_, i) => {
      const v = i + 1;
      return { variant: v, values: tableRows[String(v)] || [] };
    });
  }, [tableRows, playerCount]);

  const isLoading = rows.every(r => r.values.length === 0);
  const tickOut = outerR + 7, tickIn = outerR, labelR = outerR + 22;

  return (
    <div style={{
      display: "flex", flexDirection: "column", height: "100%", width: "100%",
      fontFamily: "'Courier New', monospace", background: "#020810", color: "#e2e8f0", overflow: "hidden",
    }}>

      {/* ── Scrollbar styles globaux ── */}
      <style>{`
        @keyframes fadeout { 0%{opacity:1} 60%{opacity:1} 100%{opacity:0} }
        @keyframes scrollpulse {
          0%, 100% { opacity: 0.35; transform: translateY(0px); }
          50% { opacity: 0.85; transform: translateY(3px); }
        }

        .rotary-scroll::-webkit-scrollbar {
          width: 4px;
        }
        .rotary-scroll::-webkit-scrollbar-track {
          background: #010810;
          border-radius: 2px;
        }
        .rotary-scroll::-webkit-scrollbar-thumb {
          background: #0a2a3a;
          border-radius: 2px;
        }
        .rotary-scroll::-webkit-scrollbar-thumb:hover {
          background: #22d3ee40;
        }
        * {
          scrollbar-width: thin;
          scrollbar-color: #0a2a3a #010810;
        }
      `}</style>

      {/* ── Bande d'indices en haut ── */}
      <div style={{
        flexShrink: 0, padding: "8px 12px",
        borderBottom: "1px solid #0a1628",
        background: "#030c1a",
      }}>
        <div style={{ display: "flex", gap: 8 }}>
          {hintCards.map((card, i) => (
            <HintCard key={i} card={card} notches={notches} revealed={cardsRevealed || allComplete} />
          ))}
        </div>
      </div>

      {/* ── Zone principale ── */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* SVG */}
        <div style={{
          flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
          position: "relative", minWidth: 0, padding: "8px",
        }}>
          {showSuccess && (
            <div style={{
              position: "absolute", inset: 0, zIndex: 10, pointerEvents: "none",
              display: "flex", alignItems: "center", justifyContent: "center",
              animation: "fadeout 1.6s ease-out forwards",
            }}>
              <span style={{ fontSize: "28px", fontWeight: 900, letterSpacing: "0.3em", color: "#34d399", textShadow: "0 0 40px #34d399" }}>
                NIVEAU VALIDÉ
              </span>
            </div>
          )}

          {testResult === "fail" && (
            <div style={{
              position: "absolute", inset: 0, zIndex: 10, pointerEvents: "none",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span style={{
                fontSize: "20px", fontWeight: 900, letterSpacing: "0.2em",
                color: "#ef4444", textShadow: "0 0 30px #ef4444",
                animation: "fadeout 1.6s ease-out forwards",
              }}>COMBINAISON INCORRECTE</span>
            </div>
          )}

          {allComplete && (
            <div style={{
              position: "absolute", inset: 0, zIndex: 10, background: "rgba(0,10,20,0.94)",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14,
            }}>
              <div style={{ fontSize: "22px", fontWeight: 900, letterSpacing: "0.3em", color: "#34d399", textShadow: "0 0 40px #34d399" }}>
                RECONFIGURATION COMPLÈTE
              </div>
              <div style={{ fontSize: "13px", color: "#34d39990", letterSpacing: "0.2em" }}>PAD BIOMÉTRIQUE RÉACTIVÉ</div>
            </div>
          )}

          <svg viewBox="0 0 500 500" style={{ width: "100%", height: "100%", maxWidth: 500, maxHeight: 500 }}>
            {Array.from({ length: notches }, (_, i) => {
              const angle = crankToAngle(i, notches), isMaj = i % 5 === 0;
              const po = polarToXY(tickOut + (isMaj ? 6 : 0), angle), pi = polarToXY(tickIn, angle);
              return <line key={`t${i}`} x1={pi.x} y1={pi.y} x2={po.x} y2={po.y}
                stroke="#3a80a0" strokeWidth={isMaj ? 2.5 : 1} strokeLinecap="round" />;
            })}
            {Array.from({ length: Math.floor((notches + 4) / 5) }, (_, i) => {
              const val = i * 5;
              if (val >= notches) return null;
              const angle = crankToAngle(val, notches), pos = polarToXY(labelR, angle);
              return <text key={`l${val}`} x={pos.x} y={pos.y}
                textAnchor="middle" dominantBaseline="middle"
                fontSize="12" fontWeight="700" fill="#5abcdc"
                style={{ fontFamily: "ui-monospace, monospace" }}>{val}</text>;
            })}
            {Array.from({ length: playerCount }, (_, i) => {
              const v = i + 1;
              return <Ring key={v} idx={i} notches={notches}
                currentCrank={cranks[String(v)] ?? 0}
                pseudo={pseudoByVariant?.[String(v)] || `J${v}`}
                completed={allComplete}
                spinAngle={isSpinning ? spinAngles[i] : undefined} />;
            })}
            <circle cx={CX} cy={CY} r={INNER_R - 8} fill="#020810" stroke="#0f2030" strokeWidth="1.5" />
            <text x={CX} y={CY} textAnchor="middle" dominantBaseline="middle"
              fontSize="9" fill="#1a5066" fontFamily="monospace" letterSpacing="0.12em">SYNC</text>
            {phase === "memorize" && (
              <g>
                <rect x={0} y={0} width={500} height={500} fill="rgba(2,8,16,0.90)" />
                <text x={CX} y={CY - 30} textAnchor="middle" fontSize="20"
                  fill="#fbbf24" fontFamily="monospace" fontWeight="900" letterSpacing="0.2em">CHARGEMENT</text>
                <text x={CX} y={CY + 26} textAnchor="middle" fontSize="60"
                  fill="#fbbf24" fontFamily="monospace" fontWeight="900">{timeLeft ?? ""}</text>
              </g>
            )}
          </svg>
        </div>

        {/* Panneau droite */}
        <div className="rotary-scroll" ref={rightPanelRef} style={{
          width: "280px", flexShrink: 0, borderLeft: "1px solid #0f2030",
          background: "#030c1a", display: "flex", flexDirection: "column",
          padding: "16px 14px", gap: 14, overflowY: "auto",
        }}>
          {/* Niveau */}
          <div>
            <div style={{ fontSize: "11px", color: "#5abcdc", letterSpacing: "0.15em", marginBottom: 8, fontWeight: "700" }}>
              NIVEAU {level}/{LEVELS.length} — {cfg.label}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {LEVELS.map((l, i) => (
                <div key={l.id} style={{
                  flex: 1, height: 5, borderRadius: 3,
                  background: i < level - 1 ? "#34d399" : i === level - 1 ? "#22d3ee" : "#0a1a28",
                  boxShadow: i === level - 1 ? "0 0 8px #22d3ee" : "none",
                }} />
              ))}
            </div>
          </div>

          {/* Tableau positions */}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "11px", color: "#5abcdc", letterSpacing: "0.15em", marginBottom: 10, fontWeight: "700" }}>
              POSITIONS CIBLES — {notches} CRANS
            </div>
            <div style={{ display: "flex", gap: 4, marginBottom: 5, paddingLeft: 28 }}>
              {["A","B","C","D","?"].map((col, ci) => (
                <div key={col} style={{
                  flex: 1, textAlign: "center", fontSize: "10px", fontWeight: "700",
                  color: ci === 4 ? "#fbbf24" : "#2a5a70", letterSpacing: "0.1em",
                }}>{col}</div>
              ))}
            </div>
            {isLoading ? (
              <div style={{ textAlign: "center", color: "#1a4a60", fontSize: "10px", padding: "20px 0" }}>— chargement —</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {rows.map((row) => {
                  const color = getColor(row.variant - 1), isMe = row.variant === Number(myVariant);
                  return (
                    <div key={row.variant} style={{
                      display: "flex", alignItems: "center", gap: 4, padding: "5px 4px", borderRadius: 6,
                      background: isMe ? `${color}20` : "rgba(0,0,0,0.15)",
                      border: isMe ? `2px solid ${color}60` : "1px solid #0a1e2a",
                      boxShadow: isMe ? `0 0 14px ${color}25` : "none",
                    }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: 4, flexShrink: 0, background: color,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "10px", fontWeight: "900", color: "#000",
                      }}>{row.variant}</div>
                      {row.values.slice(0, 4).map((val, ci) => (
                        <div key={ci} style={{
                          flex: 1, textAlign: "center", padding: "4px 2px", borderRadius: 4,
                          background: "rgba(0,0,0,0.3)", border: "1px solid #0e2030",
                          fontSize: "14px", fontWeight: "900", color: "#4a8aaa",
                        }}>{val}</div>
                      ))}
                      <div style={{
                        flex: 1, textAlign: "center", padding: "4px 2px", borderRadius: 4,
                        background: "rgba(251,191,36,0.08)", border: "2px solid #fbbf2460",
                        fontSize: "18px", fontWeight: "900", color: "#fbbf24",
                        textShadow: "0 0 10px #fbbf2460",
                      }}>?</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Boutons rotation */}
          <div style={{ padding: "12px", borderRadius: 8, border: `2px solid ${myColor}44`, background: `${myColor}10` }}>
            <div style={{ fontSize: "11px", color: myColor, letterSpacing: "0.12em", fontWeight: "700", marginBottom: 12 }}>
              MON ANNEAU — {pseudoByVariant?.[String(myVariant)] || `J${myVariant}`}
            </div>
            <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
              {["ccw", "cw"].map((dir) => {
                const active = phase === "active" && !allComplete && !isSpinning;
                return (
                  <button key={dir}
                    onClick={() => active && onRotate?.(dir)}
                    disabled={!active}
                    style={{
                      flex: 1, height: 52, borderRadius: 8,
                      cursor: active ? "pointer" : "not-allowed",
                      background: active ? `${myColor}25` : "#001122",
                      border: `2px solid ${active ? myColor + "70" : "#001a2a"}`,
                      color: active ? myColor : "#002233",
                      fontSize: "24px", fontWeight: "900", transition: "all 0.15s",
                      boxShadow: active ? `0 0 14px ${myColor}35` : "none",
                    }}
                    onMouseEnter={(e) => { if (active) e.currentTarget.style.background = `${myColor}40`; }}
                    onMouseLeave={(e) => { if (active) e.currentTarget.style.background = `${myColor}25`; }}
                  >{dir === "ccw" ? "◀" : "▶"}</button>
                );
              })}
            </div>
            <div style={{
              textAlign: "center", fontSize: "11px", letterSpacing: "0.1em", fontWeight: "700",
              color: phase === "memorize" ? "#fbbf24" : phase === "active" ? myColor : "#002233",
            }}>
              {phase === "memorize" ? `CHARGEMENT — ${timeLeft ?? ""}s`
                : phase === "active" ? "▶ ROTATION ACTIVE"
                : phase === "complete" ? "✓ TERMINÉ"
                : "EN ATTENTE"}
            </div>
          </div>

          {/* Bouton Tester */}
          <button
            onClick={handleTest} disabled={!canTest}
            style={{
              width: "100%", height: 52, borderRadius: 8,
              cursor: canTest ? "pointer" : "not-allowed",
              background: canTest ? "linear-gradient(135deg,#22d3ee18,#34d39918)" : "#001122",
              border: `2px solid ${canTest ? "#22d3ee60" : "#001a2a"}`,
              color: canTest ? "#22d3ee" : "#002233",
              fontSize: "13px", fontWeight: "900",
              letterSpacing: "0.15em", textTransform: "uppercase",
              transition: "all 0.2s",
              boxShadow: canTest ? "0 0 18px #22d3ee25" : "none",
            }}
            onMouseEnter={(e) => { if (canTest) { e.currentTarget.style.background = "linear-gradient(135deg,#22d3ee30,#34d39930)"; e.currentTarget.style.boxShadow = "0 0 28px #22d3ee40"; } }}
            onMouseLeave={(e) => { if (canTest) { e.currentTarget.style.background = "linear-gradient(135deg,#22d3ee18,#34d39918)"; e.currentTarget.style.boxShadow = "0 0 18px #22d3ee25"; } }}
          >
            {isSpinning ? "RÉINITIALISATION…" : testPending ? "VÉRIFICATION…" : "⬡ TESTER LA POSITION"}
          </button>
        </div>
      </div>
    </div>
  );
}